export const ALLOWED_TEXT_PATTERN = /^[A-Za-z0-9\s,.'/-]+$/;
export const ALLOWED_NAME_PATTERN = /^[A-Za-z\s,.'-]+$/;
export const PHONE_PATTERN = /^\+?[0-9\s]+$/;

// UK mobile validation lives in ./phone-utils (isUkMobile, UK_MOBILE_VALIDATION_MSG).
// It is backed by libphonenumber-js, which — unlike a digit pattern — can tell a
// mobile apart from a landline, a personal number (07000000000) or a freephone
// number, and treats +447… and 07… as the same number. Import it from there.

// UK postcode. Covers all six outward-code shapes (A9, A99, A9A, AA9, AA99,
// AA9A) plus the GIR 0AA special case, followed by the inward code (9AA). The
// separating space is optional so users can type `LS14AP` — normalisePostcode
// puts it back before we persist.
export const POSTCODE_PATTERN = /^([A-Z]{1,2}\d[A-Z\d]?|GIR)\s?\d[A-Z]{2}$/i;

export const TEXT_VALIDATION_MSG =
  'Only letters, numbers, spaces, commas, full stops, hyphens, apostrophes, and forward slashes are allowed';
export const NAME_VALIDATION_MSG =
  'Only letters, spaces, commas, full stops, hyphens, and apostrophes are allowed';
export const PHONE_VALIDATION_MSG =
  'Enter a valid phone number with 7–15 digits (e.g. 07911123456 or +447911123456)';
export const POSTCODE_VALIDATION_MSG = 'Enter a valid UK postcode (e.g. LS1 4AP)';

export function isValidPhone(value: string): boolean {
  if (!value || !PHONE_PATTERN.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function normalisePhone(value: string): string {
  return value.replace(/\s+/g, '');
}

export function isValidPostcode(value: string): boolean {
  return POSTCODE_PATTERN.test(value.trim());
}

/**
 * Canonical UK postcode form: uppercase, single space before the inward code
 * (the final three characters). `ls14ap` → `LS1 4AP`. Input that isn't a valid
 * postcode is returned trimmed and uppercased but otherwise untouched — callers
 * gate on isValidPostcode() first.
 */
export function normalisePostcode(value: string): string {
  const compact = value.replace(/\s+/g, '').toUpperCase();
  if (compact.length < 5) return compact;
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

/**
 * Invoice fields sent to CORE with a validation request (CWL-164). Reference is
 * letters, digits, spaces, hyphens and forward slashes, max 15 characters;
 * amount is money — at most 2 decimal places and no more than 999,999.99.
 * Over-long or punctuated values broke CORE's Validation tab. Kept deliberately
 * identical to the server-side copy in packages/contracts/src/shared/validation.ts
 * — keep both in sync.
 */
export const INVOICE_REFERENCE_PATTERN = /^[A-Za-z0-9 /-]+$/;
export const INVOICE_REFERENCE_MAX_LENGTH = 15;

/**
 * Money as typed: up to 6 integer digits and at most 2 decimal places, so the
 * largest accepted value is 999999.99. Permissive mid-typing on purpose — `12.`
 * and `.5` pass so the field can be typed into — so callers gate on
 * isValidInvoiceAmount() before submitting.
 */
export const INVOICE_AMOUNT_INPUT_PATTERN = /^\d{0,6}(\.\d{0,2})?$/;

/**
 * Deliberately short and generic: the two invoice fields sit side by side in a
 * narrow dialog, where a message spelling out the rule wrapped to three lines.
 */
export const INVOICE_REFERENCE_VALIDATION_MSG = 'Invalid reference';
export const INVOICE_AMOUNT_VALIDATION_MSG = 'Invalid amount';

export function isValidInvoiceReference(value: string): boolean {
  return value.length <= INVOICE_REFERENCE_MAX_LENGTH && INVOICE_REFERENCE_PATTERN.test(value);
}

/** Keystroke gate — may the field hold this partial value? */
export function isAllowedInvoiceAmountInput(value: string): boolean {
  return INVOICE_AMOUNT_INPUT_PATTERN.test(value);
}

/** Submit gate — is this a complete, submittable amount? */
export function isValidInvoiceAmount(value: string): boolean {
  if (value === '' || !isAllowedInvoiceAmountInput(value)) return false;
  return Number.isFinite(Number(value));
}
